import type { FunctionComponent, MouseEvent } from 'react';
import { useMemo, useRef, useEffect } from 'react';

import { withRtlSupport } from '@skyscanner-internal/backpack-web/bpk-component-icon';
import PackagesLg from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/baggage-generic';
import BeachLg from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/beach';
import CarLg from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/cars';
import FlightLg from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/flight';
import HotelLg from '@skyscanner-internal/backpack-web/bpk-component-icon/lg/hotels';
import PackagesSm from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/baggage-generic';
import BeachSm from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/beach';
import CarSm from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/cars';
import FlightSm from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/flight';
import HotelSm from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/hotels';
import BpkNavigationTabGroup from '@skyscanner-internal/backpack-web/bpk-component-navigation-tab-group';
import classNames from 'classnames';

import { TAB_ID, ANALYTICS } from '../constants';
import { behaviourEvents } from '../userTrackingEvents';
import handleClickAndTrack from '../utils/handleClickAndTrack';
import { buildCarHireUrl, buildHotelsUrl } from '../utils/urls';

import type { SearchContext, Strings, Urls } from '../types';
import type { NavigationTrackingComponent } from '../userTrackingEvents';
import type { BpkNavigationTabGroupProps } from '@skyscanner-internal/backpack-web/bpk-component-navigation-tab-group';
import type { Logger } from '@web-platform/global-components-types/utils';
import type { TrackingEventFn } from '@web-platform/global-components-utils/tracking';

import STYLES from './PrimaryNav.module.scss';

type Props = {
  activeTab: string;
  searchContext: SearchContext | undefined;
  strings: Strings;
  showPackagesTab?: boolean;
  urls: Urls;
  logger: Logger;
  trackEventFn: TrackingEventFn;
  isOpeningNewTab: boolean;
  unstable__newBubble?: boolean;
  unstable__hotelsNewBubble?: boolean;
  unstable__packagesEnableVisualChange?: boolean;
  trackingComponentName?: NavigationTrackingComponent;
};

type TabId = (typeof TAB_ID)[keyof typeof TAB_ID];

const hotelIcons = {
  desktop: withRtlSupport(HotelSm),
  mobile: withRtlSupport(HotelLg),
};

const carIcons = {
  desktop: withRtlSupport(CarSm),
  mobile: withRtlSupport(CarLg),
};

const flightIcons = {
  desktop: withRtlSupport(FlightSm),
  mobile: withRtlSupport(FlightLg),
};

const packagesIcons = {
  desktop: withRtlSupport(PackagesSm),
  mobile: withRtlSupport(PackagesLg),
};

// eslint-disable-next-line camelcase
const unstable__packagesIconsExperiment = {
  desktop: withRtlSupport(BeachSm),
  mobile: withRtlSupport(BeachLg),
};

const navId = 'PrimaryNav';

const buildTab = (
  tabId: TabId,
  text: string,
  href: string,
  icon: FunctionComponent,
  analytics: string,
  isOpeningNewTab: boolean,
  badgeText?: string,
) => ({
  id: tabId,
  text,
  href,
  icon,
  'data-cy': `${tabId}-feature`,
  'data-analytics-name': analytics,
  target: isOpeningNewTab ? '_blank' : undefined,
  badgeText,
});

const PrimaryNav = ({
  activeTab,
  isOpeningNewTab,
  logger,
  searchContext,
  showPackagesTab,
  strings,
  trackEventFn,
  trackingComponentName = 'global_navigation',
  // eslint-disable-next-line camelcase
  unstable__hotelsNewBubble = false,
  // eslint-disable-next-line camelcase
  unstable__newBubble = false,
  // eslint-disable-next-line camelcase
  unstable__packagesEnableVisualChange = false,
  urls,
}: Props) => {
  const carHireUrl = buildCarHireUrl(urls.carHire, searchContext);
  const hotelsUrl = buildHotelsUrl(urls.hotels, searchContext);
  const tabsRef = useRef<HTMLDivElement>(null);

  const isValidId = (id: string): id is TabId => ['airli', 'skhot', 'carhi', 'packa'].includes(id);

  const showBeachIcon =
    showPackagesTab &&
    // eslint-disable-next-line camelcase
    (unstable__packagesEnableVisualChange || unstable__newBubble);

  /* eslint-disable react-hooks/immutability -- TODO: WOODPECKER-4298 */
  useEffect(() => {
    const hasHotelsTab = tabs.some((tab) => tab.id === TAB_ID.HOTELS);
    const hasCarhireTab = tabs.some((tab) => tab.id === TAB_ID.CARHIRE);
    const hasPackagesTab = showPackagesTab && tabs.some((tab) => tab.id === TAB_ID.PACKAGES);

    if (hasHotelsTab) {
      const event = behaviourEvents.getImpressionEvent('hotel', trackingComponentName);
      if (event) trackEventFn([event]);
    }

    if (hasCarhireTab) {
      const event = behaviourEvents.getImpressionEvent('car_hire', trackingComponentName);
      if (event) trackEventFn([event]);
    }

    if (hasPackagesTab) {
      const event = behaviourEvents.getImpressionEvent('package', trackingComponentName);
      if (event) trackEventFn([event]);
    }
    // Emit once for each navigation tracking component. This reruns when SPA navigation moves
    // between the homepage and another page while the header remains mounted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackingComponentName]);
  /* eslint-enable react-hooks/immutability */

  // the following hook aims to always show packages tab whenever it's shown, for now it will
  // only be visible on the packages funnel (packages search & packages homepage)
  useEffect(() => {
    if (showPackagesTab && activeTab === TAB_ID.PACKAGES) {
      tabsRef.current?.scrollTo({ left: tabsRef.current.scrollWidth, behavior: 'instant' });
    }
  }, [activeTab, showPackagesTab]);

  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- TODO: WOODPECKER-4298
  const tabs = useMemo(() => {
    const baseTabs: BpkNavigationTabGroupProps['tabs'] = [
      buildTab(
        TAB_ID.FLIGHTS,
        strings.flights,
        urls.flights,
        flightIcons.desktop,
        ANALYTICS.FLIGHTS,
        isOpeningNewTab,
      ),
      buildTab(
        TAB_ID.HOTELS,
        strings.hotels,
        hotelsUrl,
        hotelIcons.desktop,
        ANALYTICS.HOTELS,
        isOpeningNewTab,
        // eslint-disable-next-line camelcase
        unstable__hotelsNewBubble ? strings.newBubble : undefined,
      ),
      buildTab(
        TAB_ID.CARHIRE,
        strings.carHire,
        carHireUrl,
        carIcons.desktop,
        ANALYTICS.CARHIRE,
        isOpeningNewTab,
      ),
    ];
    if (showPackagesTab) {
      baseTabs.push(
        buildTab(
          TAB_ID.PACKAGES,
          strings.packages ?? 'Packages',
          urls.packages,
          showBeachIcon
            ? // eslint-disable-next-line camelcase
              unstable__packagesIconsExperiment.desktop
            : packagesIcons.desktop,
          ANALYTICS.PACKAGES,
          isOpeningNewTab,
          // eslint-disable-next-line camelcase
          unstable__newBubble && !unstable__hotelsNewBubble ? strings.newBubble : undefined,
        ),
      );
    }
    return baseTabs;
  }, [
    strings.flights,
    strings.hotels,
    strings.carHire,
    strings.packages,
    strings.newBubble,
    urls.flights,
    urls.packages,
    isOpeningNewTab,
    hotelsUrl,
    carHireUrl,
    showPackagesTab,
    showBeachIcon,
    // eslint-disable-next-line camelcase
    unstable__newBubble,
    // eslint-disable-next-line camelcase
    unstable__hotelsNewBubble,
  ]);

  return (
    <div
      className={classNames(STYLES['PrimaryNav__scroll-container'], {
        [STYLES['PrimaryNav__scroll-container--with-vertical-styling']]: showPackagesTab,
        [STYLES.unstable__newBubble]:
          // eslint-disable-next-line camelcase
          unstable__hotelsNewBubble || (showPackagesTab && unstable__newBubble),
      })}
      ref={tabsRef}
    >
      <BpkNavigationTabGroup
        id={navId}
        tabs={tabs}
        onItemClick={(
          e: MouseEvent<HTMLButtonElement | HTMLAnchorElement, globalThis.MouseEvent>,
          item,
          _index,
        ) => {
          if (isValidId(item.id)) {
            handleClickAndTrack(e, item.id, 'Clicked', logger, trackEventFn, trackingComponentName);
          }
        }}
        selectedIndex={tabs.findIndex((tab) => tab.id === activeTab)}
        ariaLabel={strings.navigationTabs || 'Navigation tabs'}
      />
    </div>
  );
};

export default PrimaryNav;
